import React, { useEffect, useRef, useState } from 'react'

import deepEqual from 'fast-deep-equal'
import { api } from 'core/api/ConnectionManager'
import helpers from 'core/helpers'
import helpersFiles from 'core/helpers/_files/index'
import translate from '../../../core/translate'
import {
    CatalogFileItem,
    FolderOption,
    VideoConversionSettings,
    VideoEditorCommands,
    VideoEditorMedia,
    VideoEditorSettings,
    VideoEditorSidebarRef,
    VideoEditorTask,
} from '../videoEditor.types'

export interface IState {
    activeToggle: string
    folders: FolderOption[]
    keepProportions: boolean
    autoConvertMediaId: null | number
    activeProcess: number
    selectedItem: CatalogFileItem | null
    fonts: { id: number; name: string }[]
}
export interface IVideoEditorSidebarMethodsProps {
    minimized: boolean
    media: VideoEditorMedia
    commands: VideoEditorCommands
    defaultCommands?: VideoEditorCommands
    onChangeCommands: (commands: VideoEditorCommands) => void
    selectedTasksSourceType: string | null
    toggleMinimized: () => void
    showSettings: string | null
    settings: VideoEditorSettings
    selectedTasks: VideoEditorTask[]
    onSelectTask: (task: VideoEditorTask | null) => void
    onChangeTask: (item: VideoEditorTask, type: string) => void
    onChangeShowSettings: (show: string | null) => void
    onProcessMedia: () => void
    onChangeSettings: (value: string | number | null, field: keyof VideoEditorSettings) => void
    onSelectTasks: (tasks: VideoEditorTask[]) => void
    openMinimized: () => void
    sourceId?: string
    videoEditorSidebarRef?: React.RefObject<VideoEditorSidebarRef>
    isMobile?: boolean
    onSave: ({ id, isDirectory }: { id: number; isDirectory: string }) => void
    usePreset: boolean
    onChangeUsePreset: (usePreset: boolean) => void
    videoConversionSettings?: VideoConversionSettings
    setPresetType: (presetType: number | null) => void
    presetType: number | null
}
const VideoEditorSidebarMethods = (props: IVideoEditorSidebarMethodsProps) => {
    const [state, setState] = useState<IState>({
        activeToggle: props.minimized ? 'files' : 'tasks',
        folders: [],
        keepProportions: true,
        autoConvertMediaId: null,
        activeProcess: 0,
        selectedItem: null,
        fonts: [],
    })
    const s_ = { ...state }
    let prevProps = props
    let isLoaded: boolean = false
    const setActiveToggle = () => {
        setState((prevState) => {
            return {
                ...prevState,
                selectedItems: [],
                activeToggle: 'tasks',
                activeProcess: state.activeProcess + 1,
            }
        })
    }

    const usePrevious = <T>(value: T): T | undefined => {
        const ref = useRef<T | undefined>(undefined)
        useEffect(() => {
            ref.current = value
        })
        return ref.current
    }

    const prevPropsMinimized = usePrevious(props.minimized)

    useEffect(() => {
        api.send<{ nestedQ: boolean }, FolderOption[]>(
            'getAvailableToCreateParentSourceFolders',
            { nestedQ: false },
            { hideLoader: true }
        ).then((availableFolders) => {
            const folders = helpers.addParentKeyIdForTree(availableFolders, 'folderId') as FolderOption[]
            setState((prevState: IState) => {
                return {
                    ...prevState,
                    folders,
                }
            })
        })

        api.send<{ withCommon: boolean }, Array<{ id: number; name: string }>>('getFonts', { withCommon: true }).then(
            (res) => {
                if (res) {
                    setState((prevState) => {
                        return {
                            ...prevState,
                            fonts: res.map((font: { id: number; name: string }) => {
                                return {
                                    id: font.id,
                                    name: font.name,
                                }
                            }),
                        }
                    })
                }
            }
        )
    }, [])

    useEffect(() => {
        if (state.activeProcess > 0) {
            state.activeToggle === 'tasks' && props.onProcessMedia()
        }
        isLoaded = true
    }, [state.activeProcess, state.activeToggle])

    useEffect(() => {
        const p_ = props

        if (p_.media && !deepEqual(prevProps.media, p_.media)) {
            const availableMimeTypes = helpersFiles.availableVideoMimeTypes(p_.media.mimetype)

            if (s_.autoConvertMediaId !== p_.media.id) {
                if (availableMimeTypes) {
                    setState((prevState) => {
                        return {
                            ...prevState,
                            autoConvertMediaId: null,
                        }
                    })
                }

                const data: VideoEditorCommands = helpers.deepCopy(p_.commands)

                setState((prevState) => {
                    return {
                        ...prevState,
                        autoConvertMediaId: p_.media.id,
                    }
                })
                data.convertToMp4.active = true
                p_.onChangeCommands(data)
            }
        }

        if (!prevPropsMinimized && p_.minimized) {
            setState((prevState) => {
                return {
                    ...prevState,
                    activeToggle: 'files',
                }
            })
        }
        prevProps = p_
    }, [props])

    const setActiveSidebarTab = (value: string) => {
        setState((prevState: IState) => {
            return {
                ...prevState,
                activeToggle: value,
            }
        })
    }

    const calculateProportions = () => {
        const p_ = props
        const data: VideoEditorCommands = helpers.deepCopy(p_.commands)
        const proportionsData = {
            sourceWidth: p_.media.width,
            sourceHeight: p_.media.height,
            width: data.crop.width,
            height: data.crop.height,
        }
        const proportions = helpersFiles.calculateAspectRatioFit(proportionsData)

        data.crop.height = Math.round(proportions.height)
        data.crop.width = Math.round(proportions.width)

        p_.onChangeCommands(data)
    }
    const isButtonCheckedAndDisabled = (mimetype: string) => {
        return (
            mimetype === 'video/mp4' ||
            mimetype === 'video/ogg' ||
            mimetype === 'video/ogv' ||
            mimetype === 'application/x-troff-msvideo' ||
            mimetype === 'video/avi' ||
            mimetype === 'video/msvideo' ||
            mimetype === 'video/x-msvideo' ||
            mimetype === 'video/x-matroska' ||
            mimetype === 'video/quicktime'
        )
    }

    const onChangeCrop = (key: 'width' | 'height', value: string | number) => {
        const p_ = props
        const s_ = { ...state }
        let normalizedValue = String(value)

        if (!normalizedValue.match(/\d/g)) {
            normalizedValue = '0'
        }
        const numericValue = parseInt(normalizedValue, 10)

        const data: VideoEditorCommands = helpers.deepCopy(p_.commands)

        if (!p_.media.multiple && s_.keepProportions) {
            const proportionsData = {
                sourceWidth: p_.media.width,
                sourceHeight: p_.media.height,
                width: p_.media.width,
                height: p_.media.height,
                [key]: numericValue,
            }
            const proportions = helpersFiles.calculateAspectRatioFit(proportionsData)

            if (key === 'width') {
                data.crop.height = Math.round(proportions.height)
            } else {
                data.crop.width = Math.round(proportions.width)
            }
        }

        data.crop[key] = numericValue
        p_.onChangeCommands(data)
    }
    const onChangeNumberField = (value: string | number, key: string, field: string) => {
        const p_ = props
        const data: VideoEditorCommands = helpers.deepCopy(p_.commands)
        const normalizedValue = String(value)

        if (!normalizedValue.length) {
            data[key][field] = normalizedValue
            p_.onChangeCommands(data)
            return
        }
        if (!normalizedValue.match(/^\d+$/)) {
            return
        }

        data[key][field] = parseInt(normalizedValue, 10)
        p_.onChangeCommands(data)
    }
    /**
     * [If source type image/video was selected, disable multiple selected checkbox for other type. Accept only type, for example: image, video]
     * @method setDisabledMultiSelect
     * @param  {[object]}               source [instance of video editor task]
     */
    const setDisabledTaskMultiSelect = (source: VideoEditorTask) => {
        const { selectedTasksSourceType } = props

        if (selectedTasksSourceType) {
            return source.data.type !== selectedTasksSourceType
        }

        return false
    }
    return {
        state,
        setActiveSidebarTab,
        calculateProportions,
        isButtonCheckedAndDisabled,
        onChangeCrop,
        onChangeNumberField,
        setDisabledTaskMultiSelect,
        setState,
        setActiveToggle,
    }
}

export default VideoEditorSidebarMethods
